import CustomPagination from '@/components/custom-pagination';
import { Input } from '@/components/ui/input';
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
} from '@/components/ui/pagination';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import {
    Dispatch,
    Fragment,
    memo,
    SetStateAction,
    useEffect,
    useState,
} from 'react';
import { initialPageResponse, pageSizeOptions } from '../table-ag-constants';
import type { TableQueryParams } from '../table-ag-types';

type CustomPaginationProps = {
    /**Trang hiện tại */
    current?: number;
    /**Số item trên 1 trang */
    pageSize?: number;
    /**Show select pageSize */
    showPageSize?: boolean;
    /**setPage PageInfo */
    callback?: Dispatch<SetStateAction<TableQueryParams>>;
    /**callback show loading table */
    showTableLoading?: VoidFunction;
    /**Clear đi state local nếu faild */
    isHvError?: boolean;

    /**API DATA -> Tổng số trang */
    queryFn_totalPage?: number;
    /**API DATA -> Tổng số item */
    queryFn_totalCount?: number;
};

const TablePagination = (props: CustomPaginationProps) => {
    const {
        current = 1,
        pageSize = 10,
        queryFn_totalPage,
        queryFn_totalCount,
        showPageSize = true,
        isHvError = false,
        showTableLoading,
        callback,
    } = props;

    const transDef = useTranslations('System');

    const [pageResponse, setPageResponse] = useState<{
        totalPage: number;
        totalCount: number;
    }>(initialPageResponse);

    const [isShowPopover, setIsShowPopover] = useState(false);

    /**
     * clone lại page response từ queryFn
     */
    useEffect(() => {
        if (
            queryFn_totalCount !== undefined &&
            queryFn_totalPage !== undefined
        ) {
            setPageResponse({
                totalCount: queryFn_totalCount,
                totalPage: queryFn_totalPage || 1, // covert 0 => 1
            });
        } else if (isHvError) {
            setPageResponse(initialPageResponse);
        }
    }, [isHvError, queryFn_totalCount, queryFn_totalPage]);

    const onNext = (goToLastPage?: boolean) => {
        if (
            current === queryFn_totalPage ||
            !queryFn_totalCount ||
            queryFn_totalCount === 0
        ) {
            return;
        }
        callback?.((state) => {
            const newData =
                goToLastPage || state.pageNumber === pageResponse.totalPage
                    ? pageResponse.totalPage
                    : (state.pageNumber ?? 0) + 1;

            if (newData !== state.pageNumber) {
                showTableLoading?.();
            }
            return {
                ...state,
                pageNumber: newData,
            };
        });
    };

    const onPrev = (backToFirstPage?: boolean) => {
        if (current === 1 || !queryFn_totalCount || queryFn_totalCount === 0) {
            return;
        }
        callback?.((state) => {
            const newData = backToFirstPage
                ? 1
                : state.pageNumber === 1
                  ? state.pageNumber
                  : state.pageNumber! - 1;
            if (newData !== state.pageNumber) {
                showTableLoading?.();
            }
            return {
                ...state,
                pageNumber: newData,
            };
        });
    };

    const onChangePageSize = (data: string) => {
        callback?.((state) => {
            const newData = !isNaN(Number(data)) ? parseInt(data) : 10;
            if (newData !== state.pageSize) {
                showTableLoading?.();
            }
            return {
                ...state,
                pageSize: newData,
                pageNumber: 1,
            };
        });
    };

    const onEnterIntoPage = (page: number) => {
        callback?.((state) => {
            const newData =
                page > pageResponse.totalPage ? pageResponse.totalPage : page;

            if (newData !== state.pageNumber) {
                showTableLoading?.();
            }
            return {
                ...state,
                pageNumber: newData,
            };
        });
        setIsShowPopover(false);
    };

    const onClickPage = (page: number) => {
        if (current === page) return;

        callback?.((state) => {
            if (state.pageNumber !== page) {
                showTableLoading?.();
            }
            return {
                ...state,
                pageNumber: page,
            };
        });
    };

    const renderItem = () => {
        let isEnd = pageResponse.totalPage <= 6 ? true : false;
        const items: number[] = [];
        if (current <= 3) {
            for (let i = 1; i <= pageResponse.totalPage; i++) {
                if (i <= 6) {
                    items.push(i);
                }
            }
        } else if (current > pageResponse.totalPage - 5) {
            isEnd = true;
            for (
                let i = pageResponse.totalPage - 5;
                i <= pageResponse.totalPage;
                i++
            ) {
                i > 0 && items.push(i);
            }
        } else {
            for (let i = current - 2; i <= pageResponse.totalPage; i++) {
                if (items.length !== 6) {
                    items.push(i);
                }
            }
        }

        return (
            <Fragment>
                {items.map((idx, _i) => {
                    if (pageResponse.totalPage > 6 && _i === 2) {
                        return (
                            <Fragment key={idx}>
                                <PaginationItem
                                    className='cursor-pointer dark:border-white'
                                    key={idx}
                                >
                                    <PaginationLink
                                        className='h-8 w-8 text-[13px] dark:border-white dark:text-white'
                                        isActive={current === idx}
                                        onClick={() => {
                                            onClickPage(idx);
                                        }}
                                    >
                                        {idx}
                                    </PaginationLink>
                                </PaginationItem>
                                <Popover
                                    open={isShowPopover}
                                    onOpenChange={setIsShowPopover}
                                >
                                    <PopoverTrigger asChild>
                                        <PaginationItem className='h-8 w-8 cursor-pointer rounded hover:bg-accent dark:border-white dark:text-white'>
                                            <PaginationEllipsis className='h-8 w-8 dark:border-white dark:text-white [&>*]:h-3 [&>*]:w-3' />
                                        </PaginationItem>
                                    </PopoverTrigger>
                                    <PopoverContent className='w-52' side='top'>
                                        <div className='flex items-center gap-x-2 text-[13px]'>
                                            <span>
                                                {transDef('System_Arrive')}
                                            </span>
                                            <Input
                                                className='no-ring h-8 min-w-8 flex-1 text-[13px]'
                                                type='number'
                                                min={1}
                                                step={1}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        onEnterIntoPage(
                                                            parseInt(
                                                                e.currentTarget
                                                                    .value
                                                            ) || 1
                                                        );
                                                    }
                                                }}
                                            />
                                            <span>trang</span>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </Fragment>
                        );
                    }
                    return (
                        <PaginationItem
                            className='cursor-pointer dark:border-white'
                            key={idx}
                        >
                            <PaginationLink
                                className='h-8 w-8 text-[13px] dark:border-white dark:text-white'
                                isActive={current === idx}
                                onClick={() => {
                                    if (
                                        !queryFn_totalCount ||
                                        queryFn_totalCount === 0
                                    ) {
                                        return;
                                    }
                                    onClickPage(idx);
                                }}
                            >
                                {idx}
                            </PaginationLink>
                        </PaginationItem>
                    );
                })}
            </Fragment>
        );
    };

    const renderCalculation = () => {
        let start = (current - 1) * pageSize + 1;
        let end = start + pageSize - 1;

        if (end > pageResponse.totalCount) {
            end = pageResponse.totalCount;
        }
        if (pageResponse.totalCount === 0) {
            start = 0;
        }

        if (!start && !end && !pageResponse.totalCount)
            return <Fragment></Fragment>;
        return (
            <p className='text-[13px] font-medium'>
                {transDef('System_Show')}: {start || '-'}{' '}
                {transDef('System_Arrive').toLowerCase()} {end || '-'} /{' '}
                {pageResponse.totalCount || '-'} {transDef('System_Data')}.
            </p>
        );
    };

    return (
        <div className='flex flex-1 flex-col items-center justify-center gap-x-2 gap-y-1 lg:flex-row lg:justify-end'>
            {renderCalculation()}

            <div className='flex flex-col items-center gap-1 overflow-x-auto md:flex-row'>
                {/* pagination */}
                <Pagination className='select-none justify-end'>
                    <PaginationContent className='flex flex-wrap max-md:justify-center'>
                        {pageResponse.totalPage > 6 && (
                            <PaginationItem>
                                <CustomPagination.Previous
                                    showText={false}
                                    className={cn(
                                        'h-8 cursor-pointer bg-background text-[13px] dark:text-foreground',
                                        {
                                            'cursor-not-allowed text-gray-300 hover:bg-background hover:text-gray-200 dark:text-gray-300':
                                                current === 1,
                                        }
                                    )}
                                    aria-disabled={current === 1}
                                    title={transDef('System_Back')}
                                    onClick={() => onPrev(true)}
                                    isSkipIcon
                                />
                            </PaginationItem>
                        )}

                        <PaginationItem>
                            <CustomPagination.Previous
                                showText={false}
                                className={cn(
                                    'h-8 cursor-pointer bg-background text-[13px] dark:text-foreground',
                                    {
                                        'cursor-not-allowed text-gray-300 hover:bg-background hover:text-gray-200 dark:text-gray-300':
                                            current === 1,
                                    }
                                )}
                                aria-disabled={current === 1}
                                title={transDef('System_Back')}
                                onClick={() => onPrev()}
                            />
                        </PaginationItem>

                        <div className='flex flex-wrap items-center justify-center gap-1 max-md:order-first max-md:w-full'>
                            {renderItem()}
                        </div>

                        <PaginationItem>
                            <CustomPagination.Next
                                className={cn(
                                    'h-8 cursor-pointer bg-background text-[13px] dark:text-foreground',
                                    {
                                        'cursor-not-allowed text-gray-300 hover:bg-background hover:text-gray-200 dark:text-gray-300':
                                            current === pageResponse.totalPage,
                                    }
                                )}
                                aria-disabled={
                                    current === pageResponse.totalPage
                                }
                                title={transDef('System_Next')}
                                onClick={() => onNext()}
                                showText={false}
                            />
                        </PaginationItem>

                        {pageResponse.totalPage > 6 && (
                            <PaginationItem>
                                <CustomPagination.Next
                                    className={cn(
                                        'h-8 cursor-pointer bg-background text-[13px] dark:text-foreground',
                                        {
                                            'cursor-not-allowed text-gray-300 hover:bg-background hover:text-gray-200 dark:text-gray-300':
                                                current ===
                                                pageResponse.totalPage,
                                        }
                                    )}
                                    aria-disabled={
                                        current === pageResponse.totalPage
                                    }
                                    title={transDef('System_Next')}
                                    onClick={() => onNext(true)}
                                    showText={false}
                                    isSkipIcon
                                />
                            </PaginationItem>
                        )}

                        {showPageSize && (
                            <Select
                                value={`${pageSize && pageSize > 0 ? pageSize: pageSizeOptions[0]}` }
                                onValueChange={onChangePageSize}
                            >
                                <SelectTrigger className='no-ring h-8 w-16 text-[13px]'>
                                    <SelectValue placeholder='PageSize' />
                                </SelectTrigger>
                                <SelectContent className='w-24 min-w-24'>
                                    {pageSizeOptions.map((x, idx) => (
                                        <SelectItem
                                            key={idx}
                                            value={`${x.value}`}
                                        >
                                            {x.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </PaginationContent>
                </Pagination>
            </div>
        </div>
    );
};

export default memo(TablePagination);
